import { IStoredTranscription } from "../interfaces/IStoredTranscription";
import { HostAppSettings, HostAppSettingName } from "./HostAppSettings";
import { DocumentStorageError, DocumentStorageErrors } from "./DocumentStorageErrors";
import { LogEvents } from "../../utils/LogEvents";
import { DocumentStorageKeys } from "./telemetry/DocumentStorageKeys";
import { DurationMeasureType } from "../interfaces/ILogEvent";
import { ErrorStringFromException } from "../../utils/utils";

enum Keys {
    StoredTranscription = "StoredTranscription"
};

/*
This class is a wrapper around all things that we could store in the document. 

Since the stored items get written out to the document's custom.xml, we should ensure that any new 
settings added here go through Word's file format update process.
*/

/*
VSO:3702626 tracks following up with Word's file format team for this.
*/
export function GetStoredTranscription(): IStoredTranscription {
    try {
    return <IStoredTranscription>(JSON.parse(HostAppSettings.GetSettingAsString(HostAppSettingName.StoredTranscription)));
    } catch(e) {
        throw new DocumentStorageError(DocumentStorageErrors.CorruptTranscriptSource, ErrorStringFromException(e));
    }
}

export async function WriteStoredTranscription(storedTranscription: IStoredTranscription): Promise<void> {
    LogEvents.DocumentStorage.Start();
    LogEvents.DocumentStorage.BeginDurationMeasure(DurationMeasureType.Normal, DocumentStorageKeys.WriteStoredTranscriptionDuration);

    try {
        await Word.run(async context => {
            context.document.properties.customProperties.add(Keys.StoredTranscription, JSON.stringify(storedTranscription));
            await context.sync();
        });
        HostAppSettings.Set(HostAppSettingName.HasStoredTranscription, true);
        HostAppSettings.Set(HostAppSettingName.StoredTranscription, storedTranscription);
        LogEvents.DocumentStorage.End();
    } catch (e) {
        LogEvents.DocumentStorage.End([{key: DocumentStorageKeys.FailureReason, value: ErrorStringFromException(e)}]);
        throw e;
    }
}

export async function DeleteStoredTranscription(): Promise<void> {
    LogEvents.DocumentStorage.Start();
    LogEvents.DocumentStorage.BeginDurationMeasure(DurationMeasureType.Normal, DocumentStorageKeys.DeleteStoredTranscriptionDuration);

    try {
        await Word.run(async context => {
            const property : Word.CustomProperty | null = await GetStoredTranscriptionCustomProperty(context);
            if (property != null)
                property.delete();
        });
        HostAppSettings.Set(HostAppSettingName.HasStoredTranscription, false);
        HostAppSettings.Set(HostAppSettingName.StoredTranscription, null);
        LogEvents.DocumentStorage.End();
    } catch (e) {
        LogEvents.DocumentStorage.End([{key: DocumentStorageKeys.FailureReason, value: ErrorStringFromException(e)}]);
        throw e;
    }
}

async function GetStoredTranscriptionCustomProperty(context: Word.RequestContext): Promise<Word.CustomProperty | null> {
    try {
        LogEvents.DocumentStorage.BeginDurationMeasure(DurationMeasureType.Normal, DocumentStorageKeys.GetStoredTranscriptionCustomPropertyDuration);
        const property : Word.CustomProperty = context.document.properties.customProperties.getItem(Keys.StoredTranscription);
        property.load("type,value");
        await context.sync();
        LogEvents.DocumentStorage.EndDurationMeasure(DocumentStorageKeys.GetStoredTranscriptionCustomPropertyDuration);

        return property;
    } catch (e) {
        LogEvents.DocumentStorage.EndDurationMeasure(DocumentStorageKeys.GetStoredTranscriptionCustomPropertyDuration);
        if (e.code == 'ItemNotFound')
            return null;
        
        throw e;
    }
}